import React, { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import api, { apiErr, downloadXlsx } from "@/lib/api";
import { fmtEuroCents } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Save, Upload, FileSpreadsheet, Trophy, ClipboardList, LogOut, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function SupplierPortal() {
  const qc = useQueryClient();
  const { user, logout } = useAuth();
  const [draft, setDraft] = useState({});
  const fileRef = useRef(null);

  const { data, isLoading } = useQuery({ queryKey: ["portal-me"], queryFn: async () => (await api.get("/procurement/portal/me")).data });
  const [catFilter, setCatFilter] = useState("all");
  const [catSearch, setCatSearch] = useState("");
  const [catSearchDeb, setCatSearchDeb] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setCatSearchDeb(catSearch), 300);
    return () => clearTimeout(t);
  }, [catSearch]);
  const { data: cat } = useQuery({
    queryKey: ["portal-catalogue", catFilter, catSearchDeb],
    queryFn: async () => (await api.get(`/procurement/portal/catalogue?category=${encodeURIComponent(catFilter)}&q=${encodeURIComponent(catSearchDeb)}`)).data,
    placeholderData: keepPreviousData,
  });

  const savePrice = useMutation({
    mutationFn: async ({ item_id, price, unit }) => (await api.put("/procurement/portal/prices", { item_id, vendor_id: "self", price: Number(price) || 0, unit })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portal-me"] });
      qc.invalidateQueries({ queryKey: ["portal-catalogue"] });
      toast.success("Η τιμή καταχωρήθηκε");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const importPrices = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/procurement/portal/prices/import", fd)).data;
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["portal-me"] });
      qc.invalidateQueries({ queryKey: ["portal-catalogue"] });
      toast.success(`Ανέβηκαν ${d.saved} τιμές`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const rankTone = (r) => r === 1 ? "bg-[#EDF2E8] text-[#4A5A3E]" : r <= 3 ? "bg-[#FBF3E4] text-[#8A6A22]" : "bg-[#FEF2F2] text-[#991B1B]";

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="supplier-portal">
      <header className="bg-[#45423C] text-white">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/60">TAF · Portal Προμηθευτή</p>
            <h1 className="font-serif text-2xl">{data?.vendor?.name || user?.name}</h1>
          </div>
          <button data-testid="portal-logout" onClick={logout} className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white">
            <LogOut className="h-4 w-4" /> Αποσύνδεση
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8 space-y-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {[["items", "Είδη με τιμή", data?.stats?.items ?? "—"],
            ["best", "Είδη με καλύτερη τιμή", data?.stats?.best_price_items ?? "—"],
            ["rank", "Μέση θέση", data?.stats?.avg_rank ?? "—"]].map(([k, l, v]) => (
            <div key={k} className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid={`portal-kpi-${k}`}>
              <div className="text-[10px] uppercase tracking-widest text-[#6F6A60]">{l}</div>
              <div className="font-serif text-2xl mt-2 text-[#45423C] tabular">{v}</div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center gap-2">
            <Trophy className="h-4 w-4 text-[#C86235]" />
            <h2 className="font-medium text-[#45423C]">Οι τιμές μου</h2>
            <span className="text-xs text-[#6F6A60]">Βλέπεις τη θέση σου στη σύγκριση, όχι τους ανταγωνιστές.</span>
            <div className="ml-auto flex gap-2">
              <button data-testid="portal-template" onClick={() => downloadXlsx("/procurement/portal/template.xlsx", "Timokatalogos.xlsx")}
                className="inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] px-3 py-2 text-xs font-semibold hover:border-[#B9AE9A]">
                <FileSpreadsheet className="h-3.5 w-3.5" /> Πρότυπο Excel
              </button>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="portal-file"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) importPrices.mutate(f); e.target.value = ""; }} />
              <button data-testid="portal-import" onClick={() => fileRef.current?.click()} disabled={importPrices.isPending}
                className="inline-flex items-center gap-1.5 rounded-xl bg-[#45423C] text-white px-3 py-2 text-xs font-semibold disabled:opacity-50">
                <Upload className="h-3.5 w-3.5" /> {importPrices.isPending ? "Ανέβασμα…" : "Ανέβασμα τιμών"}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F1EDE6] text-left text-[10px] uppercase tracking-wider text-[#6F6A60]">
                  <th className="px-4 py-3">Είδος</th>
                  <th className="px-3 py-3 text-center">Μονάδα</th>
                  <th className="px-3 py-3 text-right">Τιμή μου</th>
                  <th className="px-3 py-3 text-center">Θέση</th>
                  <th className="px-3 py-3 text-right">Διαφορά από την καλύτερη</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
                {(data?.items || []).map((i) => (
                  <tr key={i.item_id} data-testid={`portal-item-${i.item_id}`} className="border-t border-[#E3DED4]">
                    <td className="px-4 py-2.5 font-medium text-[#45423C]">{i.item_name}</td>
                    <td className="px-3 py-2.5 text-center text-xs">{i.unit}</td>
                    <td className="px-3 py-2.5 text-right tabular font-semibold">{fmtEuroCents(i.price)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${rankTone(i.rank)}`}>
                        {i.rank}ος από {i.offers}
                      </span>
                    </td>
                    <td className={`px-3 py-2.5 text-right tabular ${i.diff_pct > 0 ? "text-[#991B1B]" : "text-[#6E7A63]"}`}>
                      {i.diff_pct ? `+${i.diff_pct}%` : "καλύτερη τιμή"}
                    </td>
                  </tr>
                ))}
                {!isLoading && (data?.items || []).length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-[#6F6A60]">Δεν έχεις καταχωρήσει τιμές ακόμη.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#E3DED4] space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="font-medium text-[#45423C]">Κατάλογος ειδών — καταχώρηση τιμής</h2>
              <span className="text-xs text-[#6F6A60]">Βλέπεις μόνο τα είδη που σου ανατέθηκαν για προσφορά.</span>
              <div className="relative ml-auto">
                <Search className="h-4 w-4 text-[#6F6A60] absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="portal-cat-search" value={catSearch} onChange={(e) => setCatSearch(e.target.value)} placeholder="Αναζήτηση"
                  className="rounded-xl border border-[#E3DED4] bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-[#45423C]" />
              </div>
            </div>
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar" data-testid="portal-cat-tabs">
              <button data-testid="portal-cat-all" onClick={() => setCatFilter("all")}
                className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap transition-colors ${catFilter === "all" ? "bg-[#45423C] text-white" : "bg-[#F1EDE6] text-[#6F6A60] hover:bg-[#E7E1D7]"}`}>Όλες</button>
              {(cat?.categories || []).map((c) => (
                <button key={c} data-testid={`portal-cat-${c}`} onClick={() => setCatFilter(c)}
                  className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap transition-colors ${catFilter === c ? "bg-[#45423C] text-white" : "bg-[#F1EDE6] text-[#6F6A60] hover:bg-[#E7E1D7]"}`}>{c}</button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {(cat?.rows || []).map((it) => (
                  <tr key={it.id} data-testid={`portal-cat-${it.id}`} className="border-t border-[#E3DED4]">
                    <td className="px-4 py-2">{it.name}</td>
                    <td className="px-3 py-2 text-xs text-[#6F6A60]">ανά {it.unit}</td>
                    <td className="px-3 py-2 text-right">
                      <input data-testid={`portal-price-${it.id}`} type="number" step="0.01"
                        value={draft[it.id] ?? (it.my_price ?? "")}
                        onChange={(e) => setDraft({ ...draft, [it.id]: e.target.value })}
                        placeholder="—"
                        className="w-28 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-right text-sm tabular outline-none focus:border-[#45423C]" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button data-testid={`portal-save-${it.id}`} disabled={!draft[it.id] || savePrice.isPending}
                        onClick={() => savePrice.mutate({ item_id: it.id, price: draft[it.id], unit: it.unit })}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#C86235] hover:underline disabled:opacity-30">
                        <Save className="h-3.5 w-3.5" /> Αποθήκευση
                      </button>
                    </td>
                  </tr>
                ))}
                {(cat?.rows || []).length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-[#6F6A60]" data-testid="portal-cat-empty">{(cat?.categories || []).length === 0 ? "Δεν σου έχουν ανατεθεί είδη για προσφορά ακόμη." : "Κανένα είδος για αυτά τα κριτήρια."}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-[#C86235]" />
            <h2 className="font-medium text-[#45423C]">Οι παραγγελίες μου</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F1EDE6] text-left text-[10px] uppercase tracking-wider text-[#6F6A60]">
                  <th className="px-4 py-3">Κωδικός</th>
                  <th className="px-3 py-3">Κατάσταση</th>
                  <th className="px-3 py-3 text-center">Γραμμές</th>
                  <th className="px-3 py-3 text-right">Σύνολο</th>
                  <th className="px-3 py-3">Παράδοση</th>
                </tr>
              </thead>
              <tbody>
                {(data?.orders || []).map((o) => (
                  <tr key={o.id} data-testid={`portal-order-${o.id}`} className="border-t border-[#E3DED4]">
                    <td className="px-4 py-2.5 font-mono text-xs font-semibold">{o.code}</td>
                    <td className="px-3 py-2.5">{o.status_el}</td>
                    <td className="px-3 py-2.5 text-center tabular">{(o.lines || []).length}</td>
                    <td className="px-3 py-2.5 text-right tabular font-semibold">{fmtEuroCents(o.total || 0)}</td>
                    <td className="px-3 py-2.5 text-xs text-[#6F6A60]">{o.expected_date || "—"}</td>
                  </tr>
                ))}
                {(data?.orders || []).length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-[#6F6A60]">Δεν υπάρχουν παραγγελίες.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
